import React, { useEffect, useRef, useState } from "react";
import logger from '../utils/logger';
import { showToast } from "./Toast";
import {useAPI} from '../contexts/APIContext';
import {useAuth} from '../contexts/AuthContext';
import { trackShare } from '../utils/telemetryDeck';

const ShareModal = ({ isOpen, onClose, shareUrl, type = "content", storyId = null }) => {
  const [copied, setCopied] = useState(false);
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const surface = type === "audiobook" ? "storybook" : type === "character" ? "character" : "story";
  // TelemetryDeck: opened → tapped; copied → completed; closed without a
  // copy → cancelled. The id/link only feeds the local first-vs-again check.
  const sharedThisOpen = useRef(false);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      sharedThisOpen.current = false;
      trackShare.tapped(surface);
    } else if (!isOpen && wasOpen.current && !sharedThisOpen.current) {
      trackShare.cancelled(surface);
    }
    wasOpen.current = isOpen;
  }, [isOpen, surface]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      if (!sharedThisOpen.current) trackShare.completed(surface, "copy_link", storyId || shareUrl);
      sharedThisOpen.current = true;
      setCopied(true);
      showToast("Link copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);

      if (storyId && apiClient && user?.id) {
        const timestamp = new Date().toISOString();
        const tag = type === "audiobook" ? `SHAREAUDIOBOOK_${storyId}_${timestamp}` : `SHARESTORY_${storyId}_${timestamp}`;
        apiClient.addUserDataTag(user.id, tag).then((result) => {
          if (result.status) logger.log(`Added share tag ${tag}`);
        }).catch(() => {});
      }
    } catch (error) {
      logger.error("Failed to copy:", error);
      showToast("Failed to copy link", "error");
    }
  };

  const contentLabel = type === "character" ? "Character" : "Story";

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[70] p-4"
      style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#EAF3FF",
        borderRadius: 28,
        boxShadow: "0px 5px 0px rgba(11,26,51,0.12), 0px 24px 64px rgba(11,26,51,0.24)",
        maxWidth: 460,
        width: "100%",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: "#0065F8", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" fill="none" stroke="#FFF" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
            </div>
            <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: "#0065F8", margin: 0 }}>
              Share {contentLabel}
            </h2>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: 12, background: "#FFFDF7", border: "none", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#334155" }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "16px 24px 20px" }}>
          <p style={{ color: "#334155", fontSize: 14, lineHeight: 1.5, margin: "0 0 16px" }}>
            Share this link with anyone! They can view this {contentLabel.toLowerCase()} without signing up.
          </p>

          {/* URL box */}
          <div style={{ background: "#FFFDF7", borderRadius: 14, border: "2px solid rgba(11,26,51,0.08)", boxShadow: "inset 0px 2px 4px rgba(11,26,51,0.06), 0px 2px 0px rgba(11,26,51,0.08)", padding: "4px 4px 4px 16px", display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <input
              type="text"
              readOnly
              value={shareUrl}
              onClick={(e) => e.target.select()}
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 13, color: "#334155", fontFamily: "monospace", minWidth: 0 }}
            />
            <button
              onClick={handleCopy}
              style={{
                padding: "8px 16px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Fredoka', sans-serif",
                background: copied ? "#3EDC8F" : "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
                color: copied ? "#0B1A33" : "#FFF",
                boxShadow: copied ? "0px 3px 0px #20A96A" : "0px 3px 0px #073B8E",
                display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                transition: "all 150ms ease",
              }}
            >
              {copied ? (
                <><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> Copied!</>
              ) : (
                <><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy</>
              )}
            </button>
          </div>

          {/* Info box */}
          <div style={{ background: "rgba(0,101,248,0.06)", borderRadius: 14, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
            <svg width="18" height="18" fill="none" stroke="#0065F8" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p style={{ color: "#0065F8", fontSize: 13, margin: 0, lineHeight: 1.45 }}>
              Anyone with this link can view this {contentLabel.toLowerCase()}, even without a Pixley account.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "0 24px 20px" }}>
          <button onClick={onClose} style={{ width: "100%", padding: "12px 20px", background: "#FFFDF7", color: "#334155", border: "2px solid rgba(11,26,51,0.08)", borderRadius: 18, fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.08)", fontFamily: "'Fredoka', sans-serif" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
